import React, { useState, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Header from '../../../components/common/global-top-bar';
import SwitchBar from '../../../components/switch-bar';
import { Headeroptions, PeerHeaderoptions } from '../listener-constants';
import { useListenerProfile } from '../../../hooks/usePeers';
import Spinner from '../../../components/common/spinner';
import PeerPresence from './peer-presence';
import ExperienceReview from './experience-review';
import HeadBack from '../redirect';
import { CreateExperienceModal } from '../common/create-experience-modal';
import SendOverallFeedbackModal from './send-overall-feedback-modal';
import RouteLeavingGuard from '../../../components/route-leaving-prompt';

const ContentReview: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const listenerRoleId = params.get('listenerRoleId');
  const listenerType = params.get('listenerType');
  const listenerName = params.get('listenerName');

  const { data: peerData, isLoading, refetch } = useListenerProfile(Number(listenerRoleId));
  const [isDirty, setIsDirty] = useState(false);

  const refetchPeerData = useCallback(() => {
    refetch();
  }, [refetch]);

  const headerOptions = listenerType === 'listener' ? Headeroptions : PeerHeaderoptions;
  const currentPosition = headerOptions.findIndex((opt) => opt.path.includes('content-review'));

  if (isLoading) {
    return <Spinner alignmentClass="left-64 top-30 fixed z-100 inset-0 overflow-y-auto" />;
  }

  return (
    <div className="flex h-screen flex-col">
      <Header heading={<HeadBack listenerName={listenerName || ''} listenerId={Number(listenerRoleId)} />} />
      <SwitchBar heading={headerOptions} position={currentPosition} forwardParams={true} />

      <div className="flex-1 overflow-y-auto">
        <ContentReviewHeader peerData={peerData} listenerId={Number(listenerRoleId)} />

        <div className="p-6">
          {peerData && (
            <div className="space-y-6">
              <PeerPresence peerData={peerData} refetchPeerData={refetchPeerData} onDirtyChange={setIsDirty} />
              <ExperienceReview peerData={peerData} refetchPeerData={refetchPeerData} onDirtyChange={setIsDirty} />
            </div>
          )}
        </div>
      </div>

      <RouteLeavingGuard
        when={isDirty}
        navigate={(path) => history.push(path)}
        shouldBlockNavigation={() => isDirty}
        titleText="Alert"
        contentText="You have unsaved changes. If you leave this screen without saving, your changes will be lost."
        cancelButtonText="Cancel"
        confirmButtonText="Disregard Changes"
        confirmSaveButtonText="Save Changes"
        additionalHandlePrompt={() => setIsDirty(false)}
      />
    </div>
  );
};

interface ContentReviewHeaderProps {
  peerData: any;
  listenerId: number;
}

const ContentReviewHeader: React.FC<ContentReviewHeaderProps> = ({ peerData, listenerId }) => {
  const [experienceModalType, setExperienceModalType] = useState<'audio' | 'video' | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  if (!peerData) return null;

  const fullName = `${peerData.user?.first_name || ''} ${peerData.user?.last_name || ''}`.trim();
  const displayName = peerData.user?.display_name;

  return (
    <>
      <div className="border-b border-gray-300 bg-white px-6 py-4">
        <div className="mb-3">
          <h2 className="text-xl font-bold text-gray-900">
            {fullName || 'No name'}
            {displayName && <span className="ml-2 text-base font-normal text-gray-600">({displayName})</span>}
          </h2>
          <p className="mt-1 text-xs text-gray-500">Peer ID: #{peerData.user?.id}</p>
        </div>

        <div className="flex gap-2">
          <button
            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
            onClick={() => setExperienceModalType('audio')}
          >
            Add Audio
          </button>

          <button
            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
            onClick={() => setExperienceModalType('video')}
          >
            Add Video
          </button>

          <button
            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
            onClick={() => setShowFeedbackModal(true)}
          >
            Send Overall Feedback
          </button>
        </div>
      </div>

      {experienceModalType && (
        <CreateExperienceModal
          listenerId={listenerId}
          experienceType={experienceModalType}
          visible={true}
          onClose={() => setExperienceModalType(null)}
        />
      )}

      {showFeedbackModal && (
        <SendOverallFeedbackModal
          userId={peerData?.user?.id}
          listenerEmail={peerData?.user?.email_address ?? ''}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}
    </>
  );
};

export default ContentReview;
